import { type ClubConfig } from 'shopify-types'

import { storefrontClient } from '..'
import { type GlobalRequestOptions } from '../types'

/**
 * The club is open to everyone by default. This is also the value used whenever
 * the `ClubConfig` metaobject is missing or can not be read, so a transient
 * Shopify hiccup (or a not-yet-created metaobject) never locks customers out —
 * it only ever falls back to the pre-release-gate behaviour.
 */
const DEFAULT_CLUB_CONFIG: ClubConfig = { releasePhaseActive: false }

const CLUB_CONFIG_TYPE = 'ClubConfig'

// Hand-written query (not codegen) so this stays self-contained. Mirrors the
// proven `metaobjects(type:)` shape used by fetchTierDefinitions; ClubConfig is a
// singleton, so we read the first node.
const FETCH_CLUB_CONFIG_QUERY = /* GraphQL */ `
  query FetchClubConfig($type: String!, $language: LanguageCode) @inContext(language: $language) {
    metaobjects(first: 1, type: $type) {
      nodes {
        fields {
          key
          value
        }
      }
    }
  }
`

type FetchClubConfigResponse = {
  metaobjects?: {
    nodes: Array<{ fields: Array<{ key: string; value: string | null }> }>
  } | null
}

export const fetchClubConfig = (options: GlobalRequestOptions) => async (): Promise<ClubConfig> => {
  try {
    const response = await storefrontClient.request<FetchClubConfigResponse>(
      FETCH_CLUB_CONFIG_QUERY,
      { variables: { type: CLUB_CONFIG_TYPE, language: options.language } },
    )

    const fields = response.data?.metaobjects?.nodes?.[0]?.fields
    if (!fields) return DEFAULT_CLUB_CONFIG

    // Shopify lower-cases metaobject field keys (see fetchTierDefinitions), and
    // boolean fields are serialised as the strings "true" / "false".
    const releaseField = fields.find(field => field.key.toLowerCase() === 'releasephaseactive')
    return { releasePhaseActive: releaseField?.value === 'true' }
  } catch {
    return DEFAULT_CLUB_CONFIG
  }
}
